import React, { useState, useEffect } from 'react';
import { DollarSign, TrendingUp, Phone, Bot, Calendar, Download, CreditCard } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { clientAPI } from '../../services/api';

const ClientBilling = () => {
  const { clientUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [billingData, setBillingData] = useState({
    currentMonth: {
      totalCalls: 0,
      totalMinutes: 0,
      totalAgents: 0,
      totalCost: 0
    },
    agentBreakdown: [],
    invoices: []
  });

  useEffect(() => {
    fetchBillingData();
  }, [clientUser]);

  const fetchBillingData = async () => {
    if (!clientUser?.id) return;
    
    try {
      // Fetch agents to calculate billing
      const agentsRes = await clientAPI.getAgents(clientUser?.id);
      const agents = agentsRes.data.agents || [];

      // Calculate billing based on agents
      const agentBreakdown = agents.map(agent => ({
        id: agent.id,
        name: agent.name,
        calls: agent.total_calls || 0,
        minutes: (agent.total_calls || 0) * 2.5, // Avg 2.5 min per call
        cost: ((agent.total_calls || 0) * 2.5 * 0.05).toFixed(2) // $0.05 per minute
      }));

      const totalCalls = agents.reduce((sum, a) => sum + (a.total_calls || 0), 0);
      const totalMinutes = totalCalls * 2.5;
      const totalCost = totalMinutes * 0.05;

      setBillingData({
        currentMonth: {
          totalCalls,
          totalMinutes: totalMinutes.toFixed(1),
          totalAgents: agents.length,
          totalCost: totalCost.toFixed(2)
        },
        agentBreakdown,
        invoices: [] // Empty for now
      });
    } catch (err) {
      console.error('Error fetching billing data:', err);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { icon: Phone, label: 'Total Calls', value: billingData.currentMonth.totalCalls, color: 'from-blue-500 to-blue-600' },
    { icon: TrendingUp, label: 'Minutes Used', value: billingData.currentMonth.totalMinutes, color: 'from-green-500 to-green-600' },
    { icon: Bot, label: 'Active Agents', value: billingData.currentMonth.totalAgents, color: 'from-purple-500 to-purple-600' },
    { icon: DollarSign, label: 'Current Month', value: `$${billingData.currentMonth.totalCost}`, color: 'from-teal-500 to-teal-600' },
  ];

  return (
    <div className="p-8" data-testid="client-billing-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Billing & Usage</h1>
          <p className="text-gray-400">Track your usage and manage billing</p>
        </div>
        <button className="flex items-center gap-2 bg-teal-400 hover:bg-teal-500 text-black px-4 py-2 rounded-lg font-medium transition-colors">
          <Download className="w-5 h-5" />
          Export Report
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => (
          <div
            key={index}
            className="bg-[#0d1117] border border-gray-800 rounded-xl p-6 hover:border-gray-700 transition-colors"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
            <p className="text-3xl font-bold text-white mb-1">
              {loading ? '-' : stat.value}
            </p>
            <p className="text-gray-400 text-sm">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Agent Usage Breakdown */}
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Usage by Agent</h3>
          {billingData.agentBreakdown.length === 0 ? (
            <div className="text-center py-8">
              <Bot className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500">No agents yet</p>
              <p className="text-gray-600 text-sm">Create voice agents to see usage breakdown</p>
            </div>
          ) : (
            <div className="space-y-4">
              {billingData.agentBreakdown.map((agent) => (
                <div key={agent.id} className="flex items-center justify-between p-4 bg-[#1a1a1f] rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-teal-400/10 flex items-center justify-center">
                      <Bot className="w-5 h-5 text-teal-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{agent.name}</p>
                      <p className="text-gray-500 text-sm">{agent.calls} calls · {agent.minutes} min</p>
                    </div>
                  </div>
                  <span className="text-teal-400 font-semibold">${agent.cost}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Invoice History */}
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Invoice History</h3>
          {billingData.invoices.length === 0 ? (
            <div className="text-center py-8">
              <CreditCard className="w-12 h-12 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500">No invoices yet</p>
              <p className="text-gray-600 text-sm">Invoices will appear here at the end of each billing cycle</p>
            </div>
          ) : (
            <div className="space-y-3">
              {billingData.invoices.map((invoice) => (
                <div key={invoice.id} className="flex items-center justify-between p-4 bg-[#1a1a1f] rounded-lg">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-white font-medium">{invoice.period}</p>
                      <p className="text-gray-500 text-sm">{invoice.date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-white font-semibold">${invoice.amount}</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      invoice.status === 'paid' ? 'bg-green-400/10 text-green-400' : 'bg-yellow-400/10 text-yellow-400'
                    }`}>
                      {invoice.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pricing Info */}
      <div className="mt-6 bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <h3 className="text-blue-400 font-semibold mb-2">Pricing Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-blue-400" />
            <span className="text-gray-400">Voice calls: <span className="text-white">$0.05/minute</span></span>
          </div>
          <div className="flex items-center gap-2">
            <Bot className="w-4 h-4 text-blue-400" />
            <span className="text-gray-400">Agents: <span className="text-white">Unlimited</span></span>
          </div>
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-blue-400" />
            <span className="text-gray-400">Phone numbers: <span className="text-white">$5.00/month</span></span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientBilling;
